import React, { useState, useEffect, useRef } from 'react';
import { useApi } from '../hooks/useApi';
import { useUser } from '../hooks/useUser';
import { sharedStyles, formatCurrency } from '../styles/sharedStyles';
import { getWorkDate } from '../utils/timezone';
import { formatMachineOptionLabel, formatMachineOptionLabelNoFinance, getTicketLineAmount } from '../utils/ticketLineAmount';
import { hideFinancialUi } from '../utils/foremanUi';

function sliceYmd(value) {
  if (value == null || value === '') return '';
  return String(value).slice(0, 10);
}

/** Schedule cannot start on Sunday; move to next Monday. */
function bumpSundayToMondayYmd(ymd) {
  const s = sliceYmd(ymd);
  if (!s) return '';
  const [y, m, d] = s.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return s;
  const dt = new Date(y, m - 1, d);
  while (dt.getDay() === 0) dt.setDate(dt.getDate() + 1);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

const AddTicketForm = ({ onSuccess, onClose, prefillJob }) => {
  const api = useApi();
  const { user } = useUser();
  const hideMoney = hideFinancialUi(user?.role);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('details'); // 'details' or 'images'
  const [selectedImages, setSelectedImages] = useState([]);
  const [imageDescription, setImageDescription] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [createdTicket, setCreatedTicket] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState(false);
  /** Admin-only: default on — create a draft schedule entry after the ticket is saved. */
  const [createScheduleEntry, setCreateScheduleEntry] = useState(true);
  const [scheduleCreateNote, setScheduleCreateNote] = useState('');
  
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  // Form data
  const [formData, setFormData] = useState({
    customerId: '',
    jobId: '',
    employeeId: '',
    workDate: getWorkDate(),
    workStartDate: '',
    workEndDate: '',
    description: '',
    notes: ''
  });

  // Ticket items (machines)
  const [ticketItems, setTicketItems] = useState([
    {
      machineId: '',
      employeeId: '', // Add operator selection for each machine
      rateType: 'hourly',
      hoursUsed: '',
      loadsUsed: '',
      gallonsUsed: '',
      description: '',
      notes: ''
    }
  ]);

  const [jobBudgetSummary, setJobBudgetSummary] = useState(null);
  const [jobBudgetLoading, setJobBudgetLoading] = useState(false);

  // Dropdown data
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [machines, setMachines] = useState([]);

  // Load dropdown data
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const [customersRes, jobsRes, employeesRes, machinesRes] = await Promise.all([
          api({ method: 'get', url: '/customers?limit=1000' }),
          api({ method: 'get', url: '/jobs?limit=1000' }),
          api({ method: 'get', url: '/employees?limit=1000' }),
          api({ method: 'get', url: '/machines?limit=1000' })
        ]);

        if (customersRes.data.success) setCustomers(customersRes.data.data.customers);
        if (jobsRes.data.success) setJobs(jobsRes.data.data.jobs);
        if (employeesRes.data.success) {
          const employeesData = employeesRes.data.data.employees;
          setEmployees(employeesData);
          // Set default employee ID to the first employee if available
          if (employeesData.length > 0) {
            setFormData(prev => ({ ...prev, employeeId: employeesData[0].id }));
          }
        }
        if (machinesRes.data.success) setMachines(machinesRes.data.data.machines);
      } catch (err) {
        console.error('Error loading dropdown data:', err);
        setError('Error loading form data. Please refresh and try again.');
      }
    };

    loadDropdownData();
  }, [api]);

  useEffect(() => {
    if (prefillJob) {
      setFormData(prev => ({
        ...prev,
        jobId: prefillJob.id || '',
        customerId: prefillJob.customerId || prefillJob.customer?.id || ''
      }));
    }
  }, [prefillJob]);

  // Fetch job budget summary when job changes (read-only display)
  useEffect(() => {
    const jobId = formData.jobId;
    if (!jobId) {
      setJobBudgetSummary(null);
      return;
    }

    if (user?.role === 'foreman') {
      setJobBudgetSummary(null);
      setJobBudgetLoading(false);
      return;
    }

    let cancelled = false;
    const fetchSummary = async () => {
      setJobBudgetLoading(true);
      try {
        const response = await api({ method: 'get', url: `/jobs/${jobId}/budget-summary` });
        if (response.data?.success) {
          if (!cancelled) setJobBudgetSummary(response.data.data);
        } else {
          if (!cancelled) setJobBudgetSummary(null);
        }
      } catch (err) {
        if (!cancelled) setJobBudgetSummary(null);
      } finally {
        if (!cancelled) setJobBudgetLoading(false);
      }
    };

    fetchSummary();
    return () => {
      cancelled = true;
    };
  }, [formData.jobId, api, user?.role]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTicketItemChange = (index, field, value) => {
    const updatedItems = [...ticketItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    // If rateType changes, clear fields that are no longer relevant.
    if (field === 'rateType') {
      if (value !== 'hourly') updatedItems[index].hoursUsed = '';
      if (value !== 'load') updatedItems[index].loadsUsed = '';
      if (value === 'daily') updatedItems[index].loadsUsed = '';
    }
    setTicketItems(updatedItems);
  };

  const addTicketItem = () => {
    // Use the same employee ID as the main ticket
    const defaultEmployeeId = formData.employeeId || (employees.length > 0 ? employees[0].id : '');
    setTicketItems([
      ...ticketItems,
      {
        machineId: '',
        employeeId: defaultEmployeeId, // Use same operator as main ticket
        rateType: 'hourly',
        hoursUsed: '',
        loadsUsed: '',
        gallonsUsed: '',
        description: '',
        notes: ''
      }
    ]);
  };

  const removeTicketItem = (index) => {
    if (ticketItems.length > 1) {
      const updatedItems = ticketItems.filter((_, i) => i !== index);
      setTicketItems(updatedItems);
    }
  };

  // Image management functions
  const handleImagesUploaded = (newImages) => {
    setSelectedImages(prev => [...prev, ...newImages]);
  };

  const handleImageRemoved = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length !== files.length) {
      setError('Only image files are allowed');
      return;
    }
    
    setSelectedImages(prev => [...prev, ...imageFiles]);
    setError('');
  };

  // Camera effect: start/stop stream when showCamera changes
  useEffect(() => {
    if (showCamera && videoRef.current) {
      navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
      }).then(stream => {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }).catch(err => {
        setError('Could not access camera. Please check permissions.');
      });
    }
    // Cleanup: stop camera when modal closes
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, [showCamera]);

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      
      canvas.toBlob((blob) => {
        const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
        setCapturedImage(file);
        setShowCamera(false);
      }, 'image/jpeg', 0.8);
    }
  };

  const addCapturedImage = () => {
    if (capturedImage) {
      setSelectedImages(prev => [...prev, capturedImage]);
      setCapturedImage(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate form
    if (!formData.customerId || !formData.jobId || !formData.workDate) {
      setError('Please fill in all required fields.');
      setLoading(false);
      return;
    }

    // Validate ticket items
    const validItems = ticketItems.filter(item => 
      item.machineId && 
      item.employeeId && 
      (item.rateType === 'daily' || (item.rateType === 'load' && item.loadsUsed) || (item.rateType === 'hourly' && item.hoursUsed))
    );
    if (validItems.length === 0) {
      setError('Please add at least one machine with operator and the required rate details.');
      setLoading(false);
      return;
    }

    // Use the operator from the first machine for the main ticket
    const mainTicketEmployeeId = validItems[0].employeeId;

    // Clean up ticket items for backend
    const cleanedItems = validItems.map(item => ({
      machineId: item.machineId,
      employeeId: item.employeeId, // Include operator for each machine
      rateType: item.rateType,
      hoursUsed: item.rateType === 'hourly' ? item.hoursUsed : null,
      loadsUsed: item.rateType === 'load' ? item.loadsUsed : null,
      gallonsUsed: item.gallonsUsed || 0,
      description: item.description,
      notes: item.notes
    }));

    try {
      // First, create the ticket
      const response = await api({
        method: 'post',
        url: '/tickets',
        data: {
          ...formData,
          employeeId: mainTicketEmployeeId, // Use operator from first machine
          ticketItems: cleanedItems
        }
      });

      if (response.data.success) {
        // Backend returns { ticket: {...}, email: {...} } structure
        const ticketData = response.data.data.ticket || response.data.data;
        const ticketId = ticketData.id;

        // If there are selected images, upload them
        if (selectedImages.length > 0) {
          try {
            const formData = new FormData();
            selectedImages.forEach(file => {
              formData.append('images', file);
            });
            if (imageDescription) {
              formData.append('description', imageDescription);
            }

            await api({
              method: 'post',
              url: `/api/tickets/${ticketId}/images`,
              data: formData,
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });
          } catch (imageError) {
            console.error('Error uploading images:', imageError);
            // Don't fail the ticket creation if image upload fails
            // Just show a warning - still show email modal
          }
        }

        setScheduleCreateNote('');
        if (user?.role === 'admin' && createScheduleEntry) {
          const machineIds = [...new Set(validItems.map((i) => parseInt(i.machineId, 10)).filter(Boolean))];
          const startRaw = sliceYmd(formData.workStartDate) || sliceYmd(formData.workDate);
          const startDate = bumpSundayToMondayYmd(startRaw);
          let endDate = sliceYmd(formData.workEndDate);
          if (!endDate || endDate < startDate) endDate = startDate;
          const taskDescription =
            (formData.description || '').trim() ||
            `Ticket ${ticketData.ticketNumber != null ? ticketData.ticketNumber : ticketId}`;
          if (machineIds.length && startDate) {
            try {
              const schedRes = await api({
                method: 'post',
                url: '/schedule',
                data: {
                  customerId: parseInt(formData.customerId, 10),
                  jobId: parseInt(formData.jobId, 10),
                  ticketId,
                  taskDescription,
                  startDate,
                  endDate,
                  workingDays: null,
                  bufferDays: 0,
                  machineIds,
                  status: 'Draft',
                  notificationsEnabled: true,
                  acknowledgeConflicts: true
                }
              });
              if (schedRes.data?.success) {
                const scheduleId = schedRes.data?.data?.id;
                setScheduleCreateNote(
                  scheduleId != null
                    ? `Draft schedule entry #${scheduleId} was created and linked to this ticket. You will see it on the Schedule page for the entry's dates.`
                    : 'A draft schedule entry was created and linked to this ticket. Open the Schedule page to confirm.'
                );
              } else {
                setScheduleCreateNote(
                  schedRes.data?.message
                    ? `Schedule was not created: ${schedRes.data.message}`
                    : 'Schedule was not created.'
                );
              }
            } catch (schedErr) {
              const msg =
                schedErr.response?.data?.message || schedErr.message || 'Unknown error';
              setScheduleCreateNote(`Schedule was not created: ${msg}`);
            }
          } else {
            setScheduleCreateNote(
              !machineIds.length
                ? 'Schedule was not created: no machines on the ticket.'
                : 'Schedule was not created: missing work date.'
            );
          }
        }

        // Store created ticket data and show email confirmation modal
        // Ensure we have the ID
        const customerData = customers.find(c => c.id === parseInt(formData.customerId));
        const ticketWithCustomer = {
          ...ticketData,
          id: ticketId, // Ensure ID is explicitly set
          Customer: customerData || ticketData.Customer
        };
        
        console.log('Created ticket data:', ticketWithCustomer);
        console.log('Ticket ID:', ticketWithCustomer.id);
        console.log('Customer data:', ticketWithCustomer.Customer);
        
        if (!ticketWithCustomer.id) {
          console.error('ERROR: Ticket ID is missing!', {
            ticketData,
            ticketId,
            responseData: response.data
          });
        }
        
        setCreatedTicket(ticketWithCustomer);
        setShowEmailModal(true);
        setLoading(false);
      } else {
        setError(response.data.message || 'Error creating ticket');
      }
    } catch (err) {
      console.error('Error creating ticket:', err);
      setError(err.response?.data?.message || 'Error creating ticket');
    } finally {
      setLoading(false);
    }
  };

  const getCustomerJobs = (customerId) => {
    return jobs.filter(job => job.customerId === parseInt(customerId));
  };

  // Handle email sending from confirmation modal
  const handleSendEmail = async () => {
    if (!createdTicket?.id) {
      console.error('Cannot send email: No ticket ID');
      setEmailError('Ticket ID is missing');
      return;
    }

    console.log('Sending email for newly created ticket:', createdTicket.id);
    setSendingEmail(true);
    setEmailError('');
    setEmailSuccess(false);

    try {
      console.log('Calling API: POST /tickets/' + createdTicket.id + '/send-email');
      const response = await api({
        method: 'post',
        url: `/api/tickets/${createdTicket.id}/send-email`
      });

      console.log('Email API response:', response.data);

      if (response.data.success) {
        setEmailSuccess(true);
        // Close modal after 2 seconds and call onSuccess
        setTimeout(() => {
          setShowEmailModal(false);
          setScheduleCreateNote('');
          onSuccess('Ticket created and email sent successfully!');
        }, 2000);
      } else {
        const errorMsg = response.data.message || 'Failed to send email';
        console.error('Email send failed:', errorMsg);
        setEmailError(errorMsg);
      }
    } catch (err) {
      console.error('Error sending email:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to send email';
      setEmailError(errorMessage);
    } finally {
      setSendingEmail(false);
    }
  };

  // Handle skip email
  const handleSkipEmail = () => {
    setShowEmailModal(false);
    setScheduleCreateNote('');
    onSuccess('Ticket created successfully!');
  };

  // Get customer email
  const customerEmail = createdTicket?.Customer?.email;
  const hasCustomerEmail = customerEmail && customerEmail.trim() !== '';

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div style={{
            ...sharedStyles.cardSubtitle,
            color: sharedStyles.colors.text.secondary
          }}>
            Loading form data...
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div 
        className="modal-backdrop fade show" 
        style={sharedStyles.modal?.overlay || { zIndex: 1400 }}
        onClick={onClose}
      ></div>
      <div 
        className="modal fade show" 
        style={{ display: 'flex', zIndex: 1500, alignItems: 'center', justifyContent: 'center' }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Create New Ticket</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>
            
            {/* Navigation Tabs */}
            <div className="modal-body p-0">
              <ul className="nav nav-tabs" role="tablist">
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
                    onClick={() => setActiveTab('details')}
                    type="button"
                  >
                    <i className="bi bi-file-text me-2"></i>
                    Details
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'images' ? 'active' : ''}`}
                    onClick={() => setActiveTab('images')}
                    type="button"
                  >
                    <i className="bi bi-images me-2"></i>
                    Images ({selectedImages.length})
                  </button>
                </li>
              </ul>

              <div className="p-3">
                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                {/* Details Tab */}
                {activeTab === 'details' && (
                  <form onSubmit={handleSubmit}>
                    {/* Basic Information */}
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label">Customer *</label>
                        <select
                          className="form-select"
                          name="customerId"
                          value={formData.customerId}
                          onChange={handleFormChange}
                          required
                        >
                          <option value="">Select Customer</option>
                          {[...customers].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || '', undefined, { sensitivity: 'base' })).map(customer => (
                            <option key={customer.id} value={customer.id}>
                              {customer.companyName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Job Name *</label>
                        <select
                          className="form-select"
                          name="jobId"
                          value={formData.jobId}
                          onChange={handleFormChange}
                          required
                          disabled={!formData.customerId}
                        >
                                                      <option value="">Select Job Name</option>
                          {formData.customerId && getCustomerJobs(formData.customerId).map(job => (
                            <option key={job.id} value={job.id}>
                              {job.title}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {!hideMoney && jobBudgetLoading ? (
                      <div className="alert alert-info mb-3 d-flex justify-content-between align-items-center">
                        <strong>Job Budget</strong>
                        <span className="text-muted">Loading...</span>
                      </div>
                    ) : !hideMoney && jobBudgetSummary?.hasBudget ? (
                      <div className="alert alert-info mb-3">
                        <div><strong>Job Budget:</strong> {formatCurrency(Number(jobBudgetSummary.budget || 0))}</div>
                        <div><strong>Used to Date:</strong> {formatCurrency(Number(jobBudgetSummary.usedToDate || 0))}</div>
                        <div><strong>Remaining:</strong> {formatCurrency(Number(jobBudgetSummary.remaining || 0))}</div>
                      </div>
                    ) : null}

                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label">Work Date *</label>
                        <input
                          type="date"
                          className="form-control"
                          name="workDate"
                          value={formData.workDate}
                          onChange={handleFormChange}
                          required
                        />
                        <div className="form-text">Day the ticket was submitted / work logged.</div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Creator</label>
                        <input
                          type="text"
                          className="form-control"
                          value={(user?.email || user?.name || '').trim()}
                          disabled
                          style={{ backgroundColor: '#f8f9fa' }}
                          title="Signed-in user creating this ticket"
                        />
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label">Planned work start (optional)</label>
                        <input
                          type="date"
                          className="form-control"
                          name="workStartDate"
                          value={formData.workStartDate}
                          onChange={handleFormChange}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Planned work end (optional)</label>
                        <input
                          type="date"
                          className="form-control"
                          name="workEndDate"
                          value={formData.workEndDate}
                          onChange={handleFormChange}
                        />
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label">Description</label>
                        <textarea
                          className="form-control"
                          name="description"
                          value={formData.description}
                          onChange={handleFormChange}
                          rows="2"
                          placeholder="Brief description of work performed..."
                          style={{ minHeight: '60px', maxHeight: '120px' }}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Notes</label>
                        <textarea
                          className="form-control"
                          name="notes"
                          value={formData.notes}
                          onChange={handleFormChange}
                          rows="2"
                          placeholder="Additional notes..."
                          style={{ minHeight: '60px', maxHeight: '120px' }}
                        />
                      </div>
                    </div>

                    {/* Total Gallons Display */}
                    {(() => {
                      const totalGallons = ticketItems.reduce((sum, item) => {
                        const gallons = parseFloat(item.gallonsUsed) || 0;
                        return sum + gallons;
                      }, 0);
                      return totalGallons > 0 ? (
                        <div className="alert alert-info mb-3 d-flex justify-content-between align-items-center">
                          <strong>TOTAL GALLONS USED:</strong>
                          <span className="badge bg-primary fs-6">{totalGallons.toFixed(2)} gallons</span>
                        </div>
                      ) : null;
                    })()}

                    {!hideMoney &&
                      (() => {
                        const estimatedTotal = ticketItems.reduce(
                          (sum, item) => sum + getTicketLineAmount(item, machines),
                          0
                        );
                        return estimatedTotal > 0 ? (
                          <div className="alert alert-secondary mb-3 d-flex justify-content-between align-items-center">
                            <strong>ESTIMATED TICKET TOTAL:</strong>
                            <span className="badge bg-dark fs-6">${estimatedTotal.toFixed(2)}</span>
                          </div>
                        ) : null;
                      })()}

                    {/* Ticket Items (Machines) */}
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">Equipment & Hours</h6>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={addTicketItem}
                      >
                        <i className="bi bi-plus-circle me-1"></i>
                        Add Machine
                      </button>
                    </div>

                    {ticketItems.map((item, index) => (
                      <div key={index} className="card mb-2 ticket-item-card">
                        <div className="card-body">
                          <div className="row g-2">
                            <div className="col-md-3 col-sm-6">
                              <label className="form-label">Machine *</label>
                              <select
                                className="form-select"
                                value={item.machineId}
                                onChange={(e) => handleTicketItemChange(index, 'machineId', e.target.value)}
                                required
                              >
                                <option value="">Select Machine</option>
                                {machines
                                  .sort((a, b) => a.name.localeCompare(b.name))
                                  .map(machine => (
                                    <option key={machine.id} value={machine.id}>
                                      {hideMoney
                                        ? formatMachineOptionLabelNoFinance(machine, sliceYmd(formData.workDate))
                                        : formatMachineOptionLabel(machine, sliceYmd(formData.workDate))}
                                    </option>
                                  ))}
                              </select>
                            </div>
                            <div className="col-md-3 col-sm-6">
                              <label className="form-label">Operator *</label>
                              <select
                                className="form-select"
                                value={item.employeeId}
                                onChange={(e) => handleTicketItemChange(index, 'employeeId', e.target.value)}
                                required
                              >
                                <option value="">Select Operator</option>
                                {employees.map(employee => (
                                  <option key={employee.id} value={employee.id}>
                                    {employee.name} - {employee.position}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-md-2 col-sm-6">
                              <label className="form-label">Rate Type *</label>
                              <select
                                className="form-select"
                                value={item.rateType}
                                onChange={e => handleTicketItemChange(index, 'rateType', e.target.value)}
                                required
                              >
                                <option value="hourly">Hourly</option>
                                <option value="daily">Daily</option>
                              <option value="load">Per Load</option>
                              </select>
                            </div>
                            {item.rateType === 'hourly' && (
                              <div className="col-md-2 col-sm-6">
                                <label className="form-label">Hours *</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={item.hoursUsed}
                                  onChange={e => handleTicketItemChange(index, 'hoursUsed', e.target.value)}
                                  step="0.5"
                                  min="0"
                                  required
                                />
                              </div>
                            )}
                            {item.rateType === 'load' && (
                              <div className="col-md-2 col-sm-6">
                                <label className="form-label">Loads Hauled *</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={item.loadsUsed}
                                  onChange={e => handleTicketItemChange(index, 'loadsUsed', e.target.value)}
                                  step="1"
                                  min="0"
                                  required
                                />
                              </div>
                            )}
                            <div className="col-md-2 col-sm-6">
                              <label className="form-label">Gallons Used</label>
                              <input
                                type="number"
                                className="form-control"
                                value={item.gallonsUsed}
                                onChange={e => handleTicketItemChange(index, 'gallonsUsed', e.target.value)}
                                step="0.01"
                                min="0"
                                placeholder="0.00"
                              />
                            </div>
                            <div className="col-md-1 col-sm-6 d-flex align-items-end">
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm w-100"
                                onClick={() => removeTicketItem(index)}
                                disabled={ticketItems.length === 1}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </div>
                          {!hideMoney && item.machineId ? (
                            <div className="small text-muted mt-1">
                              Line amount:{' '}
                              <strong className="text-body">
                                ${getTicketLineAmount(item, machines).toFixed(2)}
                              </strong>
                              {item.rateType === 'daily' && (
                                <span> (daily rate × 1 day)</span>
                              )}
                              {item.rateType === 'load' && (
                                <span> (loads hauled × rate)</span>
                              )}
                            </div>
                          ) : null}
                          <div className="row g-2 mt-2">
                            <div className="col-md-6 col-sm-12">
                              <label className="form-label">Description</label>
                              <input
                                type="text"
                                className="form-control"
                                value={item.description}
                                onChange={e => handleTicketItemChange(index, 'description', e.target.value)}
                                placeholder="Work description for this machine..."
                              />
                            </div>
                            <div className="col-md-6 col-sm-12">
                              <label className="form-label">Notes</label>
                              <input
                                type="text"
                                className="form-control"
                                value={item.notes}
                                onChange={e => handleTicketItemChange(index, 'notes', e.target.value)}
                                placeholder="Additional notes..."
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {user?.role === 'admin' && (
                      <div
                        className={`mb-3 rounded-3 border p-3 ${
                          createScheduleEntry
                            ? 'border-primary border-2 bg-primary bg-opacity-10'
                            : 'border-secondary border-opacity-25 bg-light'
                        }`}
                        style={{ transition: 'border-color 0.2s ease, background-color 0.2s ease' }}
                      >
                        <div className="d-flex gap-3 align-items-start">
                          <div className="form-check form-switch flex-shrink-0 pt-1">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="create-schedule-from-ticket"
                              checked={createScheduleEntry}
                              onChange={(e) => setCreateScheduleEntry(e.target.checked)}
                              disabled={loading}
                              aria-checked={createScheduleEntry}
                              aria-describedby="create-schedule-help"
                              style={{
                                width: '2.75em',
                                height: '1.35em',
                                cursor: loading ? 'not-allowed' : 'pointer'
                              }}
                            />
                          </div>
                          <div className="flex-grow-1 min-w-0">
                            <label
                              className="fw-semibold mb-1 d-flex align-items-center gap-2 text-body"
                              htmlFor="create-schedule-from-ticket"
                              style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
                            >
                              <i
                                className={`bi bi-calendar-week flex-shrink-0 ${createScheduleEntry ? 'text-primary' : 'text-secondary'}`}
                                style={{ fontSize: '1.15rem' }}
                                aria-hidden
                              />
                              Create schedule entry
                            </label>
                            <p id="create-schedule-help" className="small text-muted mb-0 mt-2 lh-sm">
                              Adds a <strong>draft</strong> row on the Schedule for this customer and job, linked to the
                              new ticket, using the machines above. Dates come from planned work if set; otherwise the
                              work date (Sundays move to Monday). Overlaps with existing bookings are acknowledged
                              automatically.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={onClose}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Creating...
                          </>
                        ) : (
                          'Create Ticket'
                        )}
                      </button>
                    </div>
                  </form>
                )}

                {/* Images Tab */}
                {activeTab === 'images' && (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Ticket Images</h6>
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <i className="bi bi-upload me-2"></i>
                          Choose Files
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-success"
                          onClick={() => setShowCamera(true)}
                        >
                          <i className="bi bi-camera me-2"></i>
                          Take Photo
                        </button>
                      </div>
                    </div>

                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />

                    <div className="mb-3">
                      <label className="form-label">Image Description (Optional)</label>
                      <textarea
                        className="form-control"
                        value={imageDescription}
                        onChange={(e) => setImageDescription(e.target.value)}
                        rows="2"
                        placeholder="Describe the images..."
                      />
                      <small className="text-muted">
                        Maximum 10 images, 10MB each. Supported formats: JPG, PNG, GIF, WebP
                      </small>
                    </div>

                    {selectedImages.length === 0 ? (
                      <div className="text-center py-4">
                        <i className="bi bi-images text-muted" style={{ fontSize: '3rem' }}></i>
                        <p className="text-muted mt-2">No images selected yet</p>
                        <p className="text-muted small">Click "Choose Files" or "Take Photo" to add images for this ticket</p>
                      </div>
                    ) : (
                      <div className="row">
                        {selectedImages.map((file, index) => (
                          <div key={index} className="col-md-4 col-lg-3 mb-3">
                            <div className="card h-100">
                              <div className="position-relative">
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={`Preview ${index + 1}`}
                                  className="card-img-top"
                                  style={{ height: '200px', objectFit: 'cover' }}
                                />
                                <div className="position-absolute top-0 end-0 p-2">
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleImageRemoved(index)}
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </div>
                              </div>
                              <div className="card-body p-2">
                                <small className="text-muted d-block">
                                  {file.name}
                                </small>
                                <small className="text-muted">
                                  {formatFileSize(file.size)}
                                </small>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={onClose}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setActiveTab('details')}
                      >
                        Continue to Details
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Camera Modal */}
      {showCamera && (
        <div className="modal fade show" style={{ display: 'block', zIndex: 1060 }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Take Photo</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCamera(false)}
                ></button>
              </div>
              <div className="modal-body text-center">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  style={{ width: '100%', maxWidth: '500px', minHeight: '300px', background: '#000' }}
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCamera(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={captureImage}
                >
                  <i className="bi bi-camera me-2"></i>
                  Capture Photo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Captured Image Review Modal */}
      {capturedImage && (
        <div className="modal fade show" style={{ display: 'block', zIndex: 1060 }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Review Captured Photo</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setCapturedImage(null)}
                ></button>
              </div>
              <div className="modal-body text-center">
                <img
                  src={URL.createObjectURL(capturedImage)}
                  alt="Captured"
                  style={{ maxWidth: '100%', maxHeight: '400px' }}
                  className="img-fluid"
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setCapturedImage(null)}
                >
                  Retake
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={addCapturedImage}
                >
                  Use This Photo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Confirmation Modal */}
      {showEmailModal && (
        <div className="modal fade show" style={{ display: 'block', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{
              borderRadius: '16px',
              border: 'none',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}>
              <div className="modal-header" style={{
                borderBottom: '1px solid #e5e9f0',
                padding: '1.5rem',
                borderRadius: '16px 16px 0 0'
              }}>
                <h5 className="modal-title" style={{
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  color: '#1a1f36'
                }}>
                  <i className="bi bi-check-circle-fill me-2" style={{ color: '#10b981' }}></i>
                  Ticket Created Successfully!
                </h5>
                {!emailSuccess && (
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleSkipEmail}
                    style={{ fontSize: '1.25rem' }}
                  ></button>
                )}
              </div>
              <div className="modal-body" style={{ padding: '1.5rem' }}>
                {emailSuccess ? (
                  <div className="text-center" style={{ padding: '1rem 0' }}>
                    <div style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      backgroundColor: '#d1fae5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1.5rem'
                    }}>
                      <i className="bi bi-envelope-check-fill" style={{ fontSize: '2rem', color: '#10b981' }}></i>
                    </div>
                    <h6 style={{
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      color: '#1a1f36',
                      marginBottom: '0.5rem'
                    }}>
                      Email Sent Successfully!
                    </h6>
                    <p style={{
                      fontSize: '0.875rem',
                      color: '#697386',
                      marginBottom: 0
                    }}>
                      The ticket has been sent to {customerEmail}
                    </p>
                  </div>
                ) : (
                  <>
                    <p style={{
                      fontSize: '1rem',
                      color: '#1a1f36',
                      marginBottom: '1.5rem'
                    }}>
                      Would you like to send this ticket via email to the customer?
                    </p>

                    {scheduleCreateNote ? (
                      <div
                        className={
                          scheduleCreateNote.startsWith('A draft schedule')
                            ? 'alert alert-success'
                            : 'alert alert-warning'
                        }
                        style={{
                          borderRadius: '8px',
                          fontSize: '0.875rem',
                          marginBottom: '1rem'
                        }}
                        role="status"
                      >
                        {scheduleCreateNote}
                      </div>
                    ) : null}
                    
                    {hasCustomerEmail ? (
                      <div style={{
                        padding: '1rem',
                        backgroundColor: '#f8fafc',
                        borderRadius: '8px',
                        border: '1px solid #e5e9f0',
                        marginBottom: '1.5rem'
                      }}>
                        <div style={{
                          fontSize: '0.875rem',
                          color: '#697386',
                          marginBottom: '0.5rem'
                        }}>
                          Customer Email:
                        </div>
                        <div style={{
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: '#1a1f36'
                        }}>
                          <i className="bi bi-envelope me-2"></i>
                          {customerEmail}
                        </div>
                      </div>
                    ) : (
                      <div style={{
                        padding: '1rem',
                        backgroundColor: '#fef3c7',
                        borderRadius: '8px',
                        border: '1px solid #fcd34d',
                        marginBottom: '1.5rem'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem'
                        }}>
                          <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '1.25rem', color: '#f59e0b' }}></i>
                          <div>
                            <div style={{
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: '#92400e',
                              marginBottom: '0.25rem'
                            }}>
                              Customer Email Not Configured
                            </div>
                            <div style={{
                              fontSize: '0.875rem',
                              color: '#78350f'
                            }}>
                              Please add an email address to the customer profile to send tickets via email.
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {emailError && (
                      <div className="alert alert-danger" style={{
                        borderRadius: '8px',
                        border: '1px solid #fecaca',
                        backgroundColor: '#fef2f2',
                        color: '#dc2626',
                        marginBottom: '1rem',
                        fontSize: '0.875rem'
                      }}>
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {emailError}
                      </div>
                    )}
                  </>
                )}
              </div>
              {!emailSuccess && (
                <div className="modal-footer" style={{
                  borderTop: '1px solid #e5e9f0',
                  padding: '1rem 1.5rem',
                  backgroundColor: '#f8fafc',
                  borderRadius: '0 0 16px 16px'
                }}>
                  <button
                    type="button"
                    className="btn"
                    onClick={handleSkipEmail}
                    disabled={sendingEmail}
                    style={{
                      backgroundColor: '#ffffff',
                      color: '#697386',
                      border: '1px solid #e5e9f0',
                      borderRadius: '8px',
                      padding: '0.5rem 1.5rem',
                      fontWeight: '600',
                      fontSize: '0.875rem'
                    }}
                  >
                    Skip
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={handleSendEmail}
                    disabled={sendingEmail || !hasCustomerEmail}
                    style={{
                      backgroundColor: hasCustomerEmail ? '#911311' : '#9ca3af',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.5rem 1.5rem',
                      fontWeight: '600',
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: hasCustomerEmail && !sendingEmail ? 'pointer' : 'not-allowed',
                      opacity: sendingEmail || !hasCustomerEmail ? 0.6 : 1
                    }}
                  >
                    {sendingEmail ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-envelope-send"></i>
                        Send Email
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ opacity: 0.5 }}></div>
        </div>
      )}
    </>
  );
};

export default AddTicketForm; 